import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { StyledButton, StyledButtonIconLeftContainer, StyledButtonIconContainer, StyledButtonLabel, StyledButtonIconRightContainer } from './Button.style.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';

const Button = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    as,
    children,
    className,
    color = 'primary',
    disabled,
    hasMaxWidth = false,
    icon,
    iconLeft,
    iconRight,
    loading,
    size,
    variant,
    ...rest
  } = _ref;
  const classNames = [className,
  // These are here to prevent breaking E2E-tests that rely on classes.
  color && !variant ? "btn-".concat(color, "--xxxxx") : undefined].filter(Boolean).join(' ');
  return /*#__PURE__*/React__default.createElement(StyledButton, _extends({
    $color: color,
    $disabled: disabled || loading,
    $hasIconLeft: !!iconLeft,
    $hasIconRight: !!iconRight,
    $hasMaxWidth: hasMaxWidth,
    $size: size,
    $variant: variant,
    className: classNames,
    disabled: disabled,
    loading: loading,
    ref: ref
  }, rest, {
    forwardedAs: as
  }), iconLeft && /*#__PURE__*/React__default.createElement(StyledButtonIconLeftContainer, null, /*#__PURE__*/cloneElement(iconLeft, {
    size: 'lg',
    ...iconLeft.props
  })), icon && /*#__PURE__*/React__default.createElement(StyledButtonIconContainer, null, /*#__PURE__*/cloneElement(icon, {
    size: 'lg',
    ...icon.props
  })), icon ? /*#__PURE__*/React__default.createElement(VisuallyHidden, null, children) : /*#__PURE__*/React__default.createElement(StyledButtonLabel, {
    className: "btn-label--xxxxx"
  }, children), iconRight && /*#__PURE__*/React__default.createElement(StyledButtonIconRightContainer, null, /*#__PURE__*/cloneElement(iconRight, {
    size: 'lg',
    ...iconRight.props
  })));
});

// @ts-expect-error
Button.displayName = 'Button';
// @ts-expect-error
Button.propTypes = {
  ariaControls: PropTypes.string,
  ariaDescribedby: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaHaspopup: PropTypes.string,
  ariaHidden: PropTypes.bool,
  /**
   * Label for assistive technology as text.
   */
  ariaLabel: PropTypes.string,
  /**
   * Label for assistive technology as id reference to an element that contains the label text.
   */
  ariaLabelledby: PropTypes.string,
  ariaPressed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Element or component used for the root node.
   */
  as: PropTypes.any,
  /**
   * Text content of the component.
   */
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * Color of the button.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'dark', 'white']),
  /**
   * Makes the component disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Limits the width of the button. Use with caution: may cut the button text.
   */
  hasMaxWidth: PropTypes.bool,
  /**
   * Label will be visually hidden and icon will show in its place.
   */
  icon: PropTypes.element,
  iconLeft: PropTypes.element,
  iconRight: PropTypes.element,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['xs']),
  tabIndex: PropTypes.number,
  type: PropTypes.string,
  variant: PropTypes.oneOf(['outline', 'plain'])
};

export { Button };
